import React from 'react';
import { Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { renderPdfElements, renderPdfElementsPlainText, parseHtmlToElements, PdfElement, imageProcessor } from '../pdfHtmlImageUtils';
import { ReportData } from '../../types/report.types';
import { isFindingClosed, getGlobalAbbreviations, normalizeSeverity, processAttachmentsToElements, getFindingStableKey, isFindingActuallyOpen } from '../../utils/findingsUtils';

interface DetailedFindingsBySeverityPageProps {
  severity: string;
  findings: any[];
  documentNumber?: string;
  reportData?: ReportData;
}

// Utility to sanitize HTML by removing only dangerous attributes while preserving styling
function sanitizeHtml(html: string): string {
  // Remove only dangerous attributes, keep style attributes for formatting
  return html.replace(/(on\w+|javascript:|data-(?!language))="[^"]*"/gi, '');
}

// Helper to format date as dd-mm-yyyy
function formatDateToDDMMYYYY(dateString: string): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

// Helper to format target and endpoints from scope string
// Input format: "AssetName > TargetValue (Environment) > endpoint1, endpoint2"
// Output format: Formatted React Node with bold labels for Target and Endpoints
function renderTargetAndEndpoints(scope: string, targetDetails?: any[], findingTargetIds?: string[], findingEndpointIds?: number[], findingEndpointValues?: string[]): React.ReactNode {
  // Primary approach: Use verified arrays of target and endpoint IDs to fetch exact values
  if (targetDetails && Array.isArray(targetDetails) && findingTargetIds && findingTargetIds.length > 0) {
    const relevantTargets = targetDetails.filter(td => findingTargetIds.includes(td.target_id));
    if (relevantTargets.length > 0) {
      const targetLabels = relevantTargets.map(t => t.target_value || t.target_name || t.url).filter(Boolean);
      const targetUrlsStr = targetLabels.length > 0 ? targetLabels.join(', ') : '';
      
      let exactEndpointsLabel = "";
      if (findingEndpointValues && findingEndpointValues.length > 0) {
          exactEndpointsLabel = findingEndpointValues.join(', ');
      } else if (findingEndpointIds && findingEndpointIds.length > 0) {
        const allFoundEndpoints: string[] = [];
        const stringifiedEndpointIds = findingEndpointIds.map(String);
        relevantTargets.forEach(rt => {
           if (rt.endpoints && Array.isArray(rt.endpoints)) {
               rt.endpoints.forEach((ep: any) => {
                   const epId = ep.resource_id || ep.id;
                   if (epId && stringifiedEndpointIds.includes(String(epId))) {
                       if (ep.value) allFoundEndpoints.push(ep.value);
                       else if (typeof ep === 'string') allFoundEndpoints.push(ep);
                   }
               });
           }
        });
        if (allFoundEndpoints.length > 0) {
           exactEndpointsLabel = allFoundEndpoints.join(', ');
        }
      }
      
      // Compute fallback legacy endpoint count if exact endpoints not found
      let endpointCountStr = "";
      if (!exactEndpointsLabel && scope && scope.includes(' | ')) {
        const [, rest] = scope.split(' | ').map(s => s.trim());
        const suffixMatch = rest?.match(/\((\d+\s+Endpoints?)\)$/i);
        if (suffixMatch) endpointCountStr = suffixMatch[1];
      }
      
      if (targetUrlsStr) {
        return (
          <>
            <Text style={{ fontWeight: 'bold', color: '#4b5563' }}>Target: </Text>
            <Text>{targetUrlsStr}</Text>
            {(exactEndpointsLabel || endpointCountStr) && (
              <>
                <Text>{'\n'}</Text>
                <Text style={{ fontWeight: 'bold', color: '#4b5563' }}>Endpoints: </Text>
                <Text>{exactEndpointsLabel || endpointCountStr}</Text>
              </>
            )}
          </>
        );
      }
    }
  }

  // Fallbacks: If targetDetails did not resolve the target, but we have a scope string, parse it.
  // This avoids displaying raw target UUIDs when the scope has not been edited.
  if (scope) {
    // New format: Asset > Target > endpoint1, endpoint2
    const parts = scope.split(' > ').map(p => p.trim());

    if (parts.length >= 3) {
      const target = parts[1];
      const endpoints = parts.slice(2).join(' > ').trim();
      return (
        <>
          <Text style={{ fontWeight: 'bold', color: '#4b5563' }}>Target: </Text>
          <Text>{target}</Text>
          <Text>{'\n'}</Text>
          <Text style={{ fontWeight: 'bold', color: '#4b5563' }}>Endpoints: </Text>
          <Text>{endpoints}</Text>
        </>
      );
    } else if (parts.length === 2) {
      // Only Asset and Target
      return (
        <>
          <Text style={{ fontWeight: 'bold', color: '#4b5563' }}>Target: </Text>
          <Text>{parts[1]}</Text>
        </>
      );
    }

    // Handle "Asset | Target_URLs (X Endpoints)" format
    if (scope.includes(' | ')) {
      const [assetPart, rest] = scope.split(' | ').map(s => s.trim());
      
      // Check if it has (X Endpoints) suffix
      const suffixMatch = rest.match(/\((\d+\s+Endpoints?)\)$/i);
      let targetUrlsStr = rest;
      let endpointCountStr = "";
      if (suffixMatch) {
         endpointCountStr = suffixMatch[1]; // "X Endpoints"
         targetUrlsStr = rest.replace(suffixMatch[0], '').trim();
      }
      
      return (
        <>
          <Text style={{ fontWeight: 'bold', color: '#4b5563' }}>Target: </Text>
          <Text>{targetUrlsStr}</Text>
          {endpointCountStr && (
            <>
              <Text>{'\n'}</Text>
              <Text style={{ fontWeight: 'bold', color: '#4b5563' }}>Endpoints: </Text>
              <Text>{endpointCountStr}</Text>
            </>
          )}
        </>
      );
    }

    // If not in new format, return as-is (legacy scope)
    return <Text>{scope}</Text>;
  }

  // If no scope string is present, but we have target IDs, display the fallback UUID labels
  if (findingTargetIds && findingTargetIds.length > 0) {
    const fallbackLabels: string[] = findingTargetIds.map(tid => {
      const match = (targetDetails || []).find((td: any) => String(td.target_id) === String(tid));
      return match
        ? (match.target_value || match.target_name || match.url || String(tid))
        : String(tid);
    }).filter(Boolean);
    if (fallbackLabels.length > 0) {
      return (
        <>
          <Text style={{ fontWeight: 'bold', color: '#4b5563' }}>Target: </Text>
          <Text>{fallbackLabels.join(', ')}</Text>
        </>
      );
    }
  }

  return null;
}

// Helper to ensure the field is always an array of PdfElement
function ensurePdfElements(field: any): PdfElement[] {
  if (!field) return [];
  if (Array.isArray(field) && field.length > 0 && typeof field[0] === 'object' && 'type' in field[0]) {
    // Already parsed
    return field;
  }
  if (typeof field === 'string') {
    // Handle HTML content properly
    if (field.trim().startsWith('<')) {
      const cleanHtml = sanitizeHtml(field);
      // Use synchronous HTML parsing to preserve formatting
      return parseHtmlToElementsSync(cleanHtml);
    }
    return [{ type: 'text', content: field }];
  }
  return [{ type: 'text', content: String(field) }];
}

// Synchronous HTML parser for PDF rendering
function parseHtmlToElementsSync(htmlString: string): PdfElement[] {
  if (!htmlString) return [];
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    // SSR fallback: return as text
    return [{ type: 'text', content: htmlString.replace(/<[^>]+>/g, '') }];
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const elements: PdfElement[] = [];

  const processNode = (node: Node, container: PdfElement[] = elements): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      const raw = node.textContent ?? '';
      const normalized = raw.replace(/\s+/g, ' ');
      if (normalized.trim().length > 0) {
        container.push({ type: 'text', content: normalized });
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      switch (el.tagName.toLowerCase()) {
        case 'p': {
          const paragraph: PdfElement = { type: 'paragraph', children: [] };
          for (const child of Array.from(el.childNodes)) {
            processNode(child, paragraph.children);
          }
          if (paragraph.children.length > 0) {
            container.push(paragraph);
          }
          break;
        }
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
          container.push({
            type: 'heading',
            level: parseInt(el.tagName.substring(1)),
            content: el.textContent?.trim() ?? ''
          });
          break;
        case 'strong':
        case 'b':
          container.push({
            type: 'text',
            content: (el.textContent ?? '').replace(/\s+/g, ' ').trim(),
            style: 'bold'
          });
          break;
        case 'em':
        case 'i':
          container.push({
            type: 'text',
            content: (el.textContent ?? '').replace(/\s+/g, ' ').trim(),
            style: 'italic'
          });
          break;
        case 'ul':
        case 'ol': {
          const isOrdered = el.tagName.toLowerCase() === 'ol';
          const startAttr = el.getAttribute('start');
          const startNumber = startAttr ? parseInt(startAttr, 10) : 1;

          const list: PdfElement & { startNumber?: number } = {
            type: 'list',
            ordered: isOrdered,
            items: [],
            startNumber: isOrdered ? startNumber : undefined
          };

          // Process direct children - can be <li> or nested <ul>/<ol> (invalid HTML but common from Word)
          let lastListItem: any = null;

          for (const child of Array.from(el.children)) {
            const childTag = (child as HTMLElement).tagName.toLowerCase();

            // Handle nested list that's a direct child of the parent list (Word-style HTML)
            if (childTag === 'ul' || childTag === 'ol') {
              // Attach this nested list to the last list item
              if (lastListItem) {
                const nestedList: PdfElement = {
                  type: 'list',
                  ordered: childTag === 'ol',
                  items: []
                };
                for (const nestedLi of Array.from((child as HTMLElement).children).filter(c => (c as HTMLElement).tagName.toLowerCase() === 'li')) {
                  const nestedItem: any = { content: [] };
                  for (const nestedChildNode of Array.from(nestedLi.childNodes)) {
                    if (nestedChildNode.nodeType === Node.TEXT_NODE) {
                      const text = nestedChildNode.textContent?.replace(/\s+/g, ' ').trim() ?? '';
                      if (text && text !== '\u00a0') {
                        nestedItem.content.push({ type: 'text', content: text });
                      }
                    } else if (nestedChildNode.nodeType === Node.ELEMENT_NODE) {
                      const nestedChildEl = nestedChildNode as HTMLElement;
                      const nestedChildTag = nestedChildEl.tagName.toLowerCase();
                      if (nestedChildTag === 'strong' || nestedChildTag === 'b') {
                        nestedItem.content.push({
                          type: 'text',
                          content: (nestedChildEl.textContent ?? '').replace(/\s+/g, ' ').trim(),
                          style: 'bold'
                        });
                      } else if (nestedChildTag === 'em' || nestedChildTag === 'i') {
                        nestedItem.content.push({
                          type: 'text',
                          content: (nestedChildEl.textContent ?? '').replace(/\s+/g, ' ').trim(),
                          style: 'italic'
                        });
                      } else {
                        processNode(nestedChildNode, nestedItem.content);
                      }
                    }
                  }
                  nestedList.items.push(nestedItem);
                }
                lastListItem.nestedList = nestedList;
              }
              continue;
            }

            // Handle regular <li> elements
            if (childTag !== 'li') continue;

            const li = child as HTMLElement;
            const listItem: any = { content: [] };
            lastListItem = listItem;

            // Process the content of this <li>, looking for nested lists
            for (const childNode of Array.from(li.childNodes)) {
              if (childNode.nodeType === Node.ELEMENT_NODE) {
                const childEl = childNode as HTMLElement;
                const tagName = childEl.tagName.toLowerCase();

                // If we find a nested list, store it separately
                if (tagName === 'ul' || tagName === 'ol') {
                  const nestedList: PdfElement = {
                    type: 'list',
                    ordered: tagName === 'ol',
                    items: []
                  };
                  // Recursively process nested list items
                  for (const nestedLi of Array.from(childEl.children).filter(child => child.tagName.toLowerCase() === 'li')) {
                    const nestedItem: any = { content: [] };
                    // Process nested li content - recursively handle all child nodes
                    for (const nestedChild of Array.from(nestedLi.childNodes)) {
                      if (nestedChild.nodeType === Node.TEXT_NODE) {
                        const text = nestedChild.textContent?.replace(/\s+/g, ' ').trim() ?? '';
                        if (text && text !== '\u00a0') {
                          nestedItem.content.push({ type: 'text', content: text });
                        }
                      } else if (nestedChild.nodeType === Node.ELEMENT_NODE) {
                        const nestedChildEl = nestedChild as HTMLElement;
                        const nestedChildTag = nestedChildEl.tagName.toLowerCase();
                        // Handle deeply nested lists recursively
                        if (nestedChildTag === 'ul' || nestedChildTag === 'ol') {
                          processNode(nestedChild, nestedItem.content);
                        } else if (nestedChildTag === 'strong' || nestedChildTag === 'b') {
                          nestedItem.content.push({
                            type: 'text',
                            content: (nestedChildEl.textContent ?? '').replace(/\s+/g, ' ').trim(),
                            style: 'bold'
                          });
                        } else if (nestedChildTag === 'em' || nestedChildTag === 'i') {
                          nestedItem.content.push({
                            type: 'text',
                            content: (nestedChildEl.textContent ?? '').replace(/\s+/g, ' ').trim(),
                            style: 'italic'
                          });
                        } else {
                          processNode(nestedChild, nestedItem.content);
                        }
                      }
                    }
                    nestedList.items.push(nestedItem);
                  }
                  listItem.nestedList = nestedList;
                } else {
                  // Process other elements normally (text, bold, italic, etc.)
                  processNode(childNode, listItem.content);
                }
              } else if (childNode.nodeType === Node.TEXT_NODE) {
                const text = childNode.textContent?.replace(/\s+/g, ' ').trim() ?? '';
                if (text && text !== '\u00a0') { // Skip non-breaking spaces
                  listItem.content.push({ type: 'text', content: text });
                }
              }
            }

            list.items.push(listItem);
          }
          if (list.items.length > 0) {
            container.push(list);
          }
          break;
        }
        case 'pre':
        case 'code':
          // Handle code blocks - preserve content but mark as code
          container.push({
            type: 'code',
            content: el.textContent?.trim() ?? '',
            language: el.getAttribute('data-language') || 'code'
          } as any);
          break;
        case 'blockquote':
          container.push({
            type: 'blockquote',
            content: el.textContent?.trim() ?? ''
          } as any);
          break;
        case 'img': {
          const src = el.getAttribute('src');
          const width = el.getAttribute('width');
          const height = el.getAttribute('height');
          // Check if the image was pre-warmed into the cache (base64 data URI)
          // If not cached, pass raw URL — react-pdf will attempt fetch (may fail on CORS)
          const cachedSrc = src ? imageProcessor.getCached(src) : undefined;
          container.push({
            type: 'image',
            src: cachedSrc !== undefined ? cachedSrc : src,
            alt: el.getAttribute('alt') || '',
            width: width ?? undefined,
            height: height ?? undefined,
          });
          break;
        }
        case 'br':
          container.push({ type: 'break' });
          break;
        default:
          // For other elements, process children
          for (const child of Array.from(el.childNodes)) {
            processNode(child, container);
          }
          break;
      }
    }
  };

  for (const child of Array.from(doc.body.childNodes)) {
    processNode(child);
  }
  return elements;
}

const styles = StyleSheet.create({
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 0,
    right: 0,
    fontSize: 10,
    color: 'grey',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  footerLeft: {
    textAlign: 'left',
    flex: 1,
  },
  footerRight: {
    textAlign: 'right',
    flex: 1,
  },
});

const DetailedFindingsBySeverityPage: React.FC<DetailedFindingsBySeverityPageProps & { registerSectionPage?: (section: string, page: number) => void, sectionId?: string }> = ({ severity, findings, documentNumber, registerSectionPage, sectionId, reportData }) => {
  const severityColors: Record<string, { text: string; badge: string }> = {
    Critical: { text: '#b91c1c', badge: '#b91c1c' }, // red-700
    High: { text: '#ea580c', badge: '#ea580c' },     // orange-600
    Medium: { text: '#ca8a04', badge: '#ca8a04' },   // yellow-600
    Low: { text: '#10b981', badge: '#10b981' },      // green
    Info: { text: '#64748b', badge: '#64748b' },     // grey
  };
  let normalizedSeverity = severity || 'Info';
  if (normalizedSeverity.toLowerCase() === 'none') normalizedSeverity = 'Info';
  const colorKey = (normalizedSeverity.charAt(0).toUpperCase() + normalizedSeverity.slice(1).toLowerCase()) as keyof typeof severityColors;
  const titleColor = severityColors[colorKey]?.text || '#2563eb';
  const badgeColor = severityColors[colorKey]?.badge || '#2563eb';

  // Filter out closed findings based on config
  const config = reportData?.finding_config;
  
  const visibleFindings = (findings || []).filter((f, idx) => {
    const idStr = (f as any)._stableKey || getFindingStableKey(f, idx);
    const indConfig = config?.individualFindings?.[idStr];
    
    if (indConfig) {
      return indConfig.included;
    }
    
    // Default behavior: 
    // Only include truly OPEN findings if no specific configuration exists.
    // This applies to both legacy reports (no config) and new reports (initial state).
    return isFindingActuallyOpen(reportData, f);
  });

  // Helper to get effective field config for a finding
  const getEffectiveFieldConfig = (finding: any, field: string) => {
    const idStr = (finding as any)._stableKey;
    const indConfig = config?.individualFindings?.[idStr];
    
    if (indConfig && indConfig.fields) {
      return (indConfig.fields as any)[field] !== false;
    }
    
    if (config && config.globalFields) {
      return (config.globalFields as any)[field] !== false;
    }

    // Legacy fallback
    if (config && (config as any).fields) {
      return ((config as any).fields as any)[field] !== false;
    }
    
    return true;
  };
  // If none remain, render nothing
  if (visibleFindings.length === 0) {
    return null;
  }
  // Render each finding on a new Page
  return (
    <>
      {(() => {
        const abbrMap = getGlobalAbbreviations(reportData);
        const sortedVisible = [...visibleFindings].sort((a, b) => {
          const aTitleKey = (a.title || '').toLowerCase().trim();
          const bTitleKey = (b.title || '').toLowerCase().trim();
          const aAbbr = abbrMap[aTitleKey] || a.abbreviation || '';
          const bAbbr = abbrMap[bTitleKey] || b.abbreviation || '';
          return aAbbr.localeCompare(bAbbr, undefined, { numeric: true });
        });

        return sortedVisible.map((finding, idx) => {
          const titleKey = (finding.title || '').toLowerCase().trim();
          const abbr = abbrMap[titleKey] || finding.abbreviation || '??';

          return (
            <Page key={idx} size="A4" id={idx === 0 ? sectionId : undefined} style={{
              flexDirection: 'column',
              backgroundColor: '#ffffff',
              padding: '20mm 8mm',
              fontFamily: 'Helvetica',
              fontSize: 12,
            }}>
              {/* Hidden Text to register section page for TOC (only on first finding) */}
              {registerSectionPage && idx === 0 && (
                <Text style={{ display: 'none', fontSize: 1 }} render={({ pageNumber }) => {
                  registerSectionPage(`DetailedFindings_${severity}`, pageNumber);
                  return '';
                }} />
              )}
              <View style={{ paddingHorizontal: 24, flexDirection: 'column', flex: 1 }}>
                <View style={{ flex: 1 }}>
                  {idx === 0 && (
                    <Text style={{ fontSize: 16, fontWeight: 'bold', color: titleColor, marginBottom: 12, lineHeight: 1.4 }}>
                      {severity.toUpperCase()} SEVERITY FINDINGS
                    </Text>
                  )}
                  <View style={{ backgroundColor: '#fff', borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10 }}>
                        {getEffectiveFieldConfig(finding, 'abbreviation') && (
                          <View style={{ backgroundColor: badgeColor, color: '#fff', borderRadius: 4, paddingHorizontal: 8, paddingVertical: 2, marginRight: 10 }}>
                            <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12, lineHeight: 1.4 }}>{abbr}</Text>
                          </View>
                        )}
                        {getEffectiveFieldConfig(finding, 'title') && (
                          <Text style={{
                            fontWeight: 'bold',
                            fontSize: 14,
                            color: '#1f2937',
                            maxWidth: '80%',
                            lineHeight: 1.4,
                            textAlign: 'justify'
                          }}>{finding.title}</Text>
                        )}
                      </View>
                    {(finding.scope || (finding.target_ids && finding.target_ids.length > 0)) && getEffectiveFieldConfig(finding, 'scope') && (
                      <View style={{ marginBottom: 12 }}>
                        <Text style={{ fontWeight: 'bold', fontSize: 13, color: '#374151', marginBottom: 6, lineHeight: 1.4 }}>I. Vulnerable Target</Text>
                        <Text style={{
                          fontSize: 12,
                          color: '#6b7280',
                          lineHeight: 1.5,
                          textAlign: 'left'
                        }}>
                          {renderTargetAndEndpoints(finding.scope, reportData?.target_details, finding.target_ids, finding.endpoint_ids, finding.endpoint_values)}
                        </Text>
                      </View>
                    )}
                    {finding.description && getEffectiveFieldConfig(finding, 'description') && (
                      <View style={{ marginBottom: 12 }}>
                        <Text style={{ fontWeight: 'bold', fontSize: 13, color: '#374151', marginBottom: 6, lineHeight: 1.4 }}>II. Description</Text>
                        <View style={{
                          fontSize: 12,
                          lineHeight: 1.4,
                          textAlign: 'justify'
                        }}>{renderPdfElements(ensurePdfElements(finding.description))}</View>
                      </View>
                    )}
                    {finding.instructions && getEffectiveFieldConfig(finding, 'instructions') && (
                      <View style={{ marginBottom: 12 }}>
                        <Text style={{ fontWeight: 'bold', fontSize: 13, color: '#374151', marginBottom: 6, lineHeight: 1.4 }}>III. Steps to Reproduce</Text>
                        <View style={{
                          fontSize: 12,
                          lineHeight: 1.4,
                          textAlign: 'justify'
                        }}>{renderPdfElements(ensurePdfElements(finding.instructions))}</View>
                      </View>
                    )}
                    {finding.impact && getEffectiveFieldConfig(finding, 'impact') && (
                      <View style={{ marginBottom: 12 }}>
                        <Text style={{ fontWeight: 'bold', fontSize: 13, color: '#374151', marginBottom: 6, lineHeight: 1.4 }}>IV. Impact</Text>
                        <View style={{
                          fontSize: 12,
                          lineHeight: 1.4,
                          textAlign: 'justify'
                        }}>{renderPdfElements(ensurePdfElements(finding.impact))}</View>
                      </View>
                    )}
                    {finding.fix && getEffectiveFieldConfig(finding, 'fix') && (
                      <View style={{ marginBottom: 12 }}>
                        <Text style={{ fontWeight: 'bold', fontSize: 13, color: '#374151', marginBottom: 6, lineHeight: 1.4 }}>V. Recommendation</Text>
                        <View style={{
                          fontSize: 12,
                          lineHeight: 1.4,
                          textAlign: 'justify'
                        }}>{renderPdfElements(ensurePdfElements(finding.fix))}</View>
                      </View>
                    )}
                    {finding.attachments && getEffectiveFieldConfig(finding, 'evidence') && (
                      <View style={{ marginBottom: 12 }}>
                        <Text style={{ fontWeight: 'bold', fontSize: 13, color: '#374151', marginBottom: 6, lineHeight: 1.4 }}>VI. Evidence / Screenshots</Text>
                        <View style={{
                          marginTop: 8,
                          gap: 12
                        }}>{renderPdfElements(processAttachmentsToElements(finding.attachments))}</View>
                      </View>
                    )}
                    {/* <View>
                    <Text style={{ fontWeight: 'bold', fontSize: 15, color: '#374151', marginBottom: 6 }}>VI. Submission Date</Text>
                    <Text style={{ fontSize: 12, color: '#6b7280' }}>{formatDateToDDMMYYYY(finding.submitted_date)}</Text>
                  </View> */}
                  </View>
                </View>
              </View>
              <View style={styles.footer} fixed>
                <Text style={styles.footerLeft}>{documentNumber || 'Document Number'}</Text>
                <Text style={styles.footerRight} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
              </View>
            </Page>
          );
        });
      })()}
    </>
  );
};

export default DetailedFindingsBySeverityPage; 